/**
 * PDF related apis
 */

import axios from 'axios';
import { request } from './request';
import { CURR_API } from '@/constants/apis';
import {
  getFileUploadRequestConfig,
  getRequestConfig,
  getBlobRequestConfig
} from './config';
import {
  PDF_UPLOAD_ACTION,
  SAVE_PDF_ACTION,
  INSERT_GRAB_ACTION,
  GET_GRABBED_IMAGE_ACTION,
  ADD_EXTRACTION_ACTION,
  SAVE_EXTRACTION_ACTION,
  SEARCH_NPI_ACTION,
  RESOLVE_NPI_ACTION,
  SEARCH_CATEGORY_ACTION,
  SEARCH_PROCEDURE_ACTION,
  GET_PREVIEW_ACTION, 
  GET_GRABBED_PAGE_ACTION
} from './ajaxActions';
import {
  UPLOAD_PDF_SUCCESS_MESSAGE,
  GET_GRABBED_IMAGE_SUCCESS_MESSAGE,
  SAVE_EXTRACTION_SUCCESS_MESSAGE,
  GET_PREVIEW_SUCCESS_MESSAGE, 
  MISSING_CATEGORY_OR_PROCEDURE_MESSAGE
} from './ajaxActionResponse';
import { parsePdf, bytesFromBlob } from './file/fileUtil';
import { downloadPdfDocument } from '@/api2/documents';

/**
 * Uploads pdf to database
 * upload success reponse from server: {'message' : 'pdfUploadedMessage', 'id' : $idPDF, 'data': $allData}
 * @param {object} formData multipart form data, contains pdf
 */
export function uploadPdf (formData) {
  const config = getFileUploadRequestConfig(formData, PDF_UPLOAD_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
    .then( (response) => {
      if (response && response.data.message === UPLOAD_PDF_SUCCESS_MESSAGE) {
        resolve(response);
      } else {
        reject(`unable to upload PDF: ${(response?response.data.message:'no reponse from server')}`);
      }
    })
    .catch( (err) => {
      reject(err);
    });
  });
}

/**
 * Retrieves PDF as a document for viewing.
 *
 * `GET /pdf-documents/{pdf}/content`, not pacmny-be's `/file/pdfs` with action
 * `requestPdf`. That pair had no bridge entry — its CDA sibling
 * (`/file/cdas#requestCda`) is mapped and these two were missed — so every
 * lookup threw `BridgeUnmappedOperation` and the routed `/pdfs` viewer rendered
 * nothing, while the *upload* half stayed mapped: a user could file a PDF they
 * could never open again.
 *
 * The bytes come through the API with the Bearer token, never from a path under
 * the web root. The legacy tree was served as static files, so any stored
 * document was readable by anyone who could guess a path, in any organization,
 * with no token and no audit record.
 *
 * @param {number|string} id id of the PDF document to request
 * @returns {Promise<{pdf: Uint8Array}>}
 */
export function getPdfById(id) {
  return downloadPdfDocument(id)
    .then(({ blob }) => bytesFromBlob(blob))
    .then((pdf) => ({ pdf }));
}

/**
 * @returns url to cropped image (or is it the stream)
 */
export function getGrabbedImage(requestParams) {
  const config = getRequestConfig(requestParams, GET_GRABBED_IMAGE_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response && response.data && response.data.message === GET_GRABBED_IMAGE_SUCCESS_MESSAGE) {
        resolve(response.data.url);
      } else {
        reject(`unable to get grab image: ${JSON.stringify(response)}`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
}

/**
 * Inserts grabbed image to a page (specified in the request params)
 */
export function insertGrabRequest(requestParams) {
  const config = getRequestConfig(requestParams, INSERT_GRAB_ACTION);
    return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response) {
        resolve();
      } else {
        reject(`unable to insert grabbed image: ${JSON.stringify(response)}`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
}

/** 
 * Saves the page number of the pdf last viewed
 */
export function savePages(requestParams) {
  const config = getRequestConfig(requestParams, SAVE_PAGE_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response) {
        resolve();
      } else {
        reject(`unable to save page: ${JSON.stringify(response)}`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
}

// Split NPILookup "name" field into first/last for the save modal.
function splitProviderName(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { first_name: '', last_name: '' };
  }
  if (parts.length === 1) {
    return { first_name: '', last_name: parts[0] };
  }
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

// Parse a single-line registry address string into structured fields.
function parseRegistryAddress(addressStr) {
  const raw = (addressStr || '').trim();
  if (!raw) {
    return {
      address_first_line: '',
      address_city: '',
      address_state: '',
    };
  }
  const commaIdx = raw.indexOf(',');
  if (commaIdx === -1) {
    return { address_first_line: raw, address_city: '', address_state: '' };
  }
  const address_first_line = raw.slice(0, commaIdx).trim();
  const rest = raw.slice(commaIdx + 1).trim();
  const restParts = rest.split(/\s+/).filter(Boolean);
  const address_state = restParts.length > 1 ? restParts[restParts.length - 2] : '';
  const address_city = restParts.length > 0
    ? restParts.slice(0, Math.max(0, restParts.length - 2)).join(' ')
    : '';
  return { address_first_line, address_city, address_state };
}

// Prefer LOCATION then PRIMARY address from CMS registry results.
function pickBestRegistryAddress(addresses) {
  if (!Array.isArray(addresses) || addresses.length === 0) {
    return {};
  }
  for (const purpose of ['LOCATION', 'PRIMARY']) {
    const match = addresses.find(
      (a) => (a.address_purpose || '').toUpperCase() === purpose
    );
    if (match) {
      return match;
    }
  }
  return addresses[0];
}

// Map one NPILookup address object to save-modal provider fields.
function mapRegistryAddress(addr) {
  if (addr.address_1 || addr.city || addr.state) {
    return {
      address_first_line: addr.address_1 || '',
      address_second_line: addr.address_2 || '',
      address_city: addr.city || '',
      address_state: addr.state || '',
      phone: addr.tel || '',
      fax: addr.fax || '',
    };
  }
  const parsed = parseRegistryAddress(addr.address);
  return {
    address_first_line: parsed.address_first_line,
    address_second_line: '',
    address_city: parsed.address_city,
    address_state: parsed.address_state,
    phone: addr.tel || '',
    fax: addr.fax || '',
  };
}

// Normalize NPILookup.php JSON into provider rows for the PDF save modal.
export function mapRegistryNpiResults(data) {
  if (!data) {
    return [];
  }
  if (data.results === 'No Results Found' || data.results === 'No Reuslts Found') {
    return [];
  }
  if (data.msg) {
    return [];
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => {
    const addr = pickBestRegistryAddress(row.addresses);
    const mappedAddr = mapRegistryAddress(addr);
    const names = splitProviderName(row.name);
    const npinumber = String(row.npinumber || '');
    return {
      id: npinumber,
      npinumber,
      first_name: row.first_name || names.first_name,
      last_name: row.last_name || names.last_name,
      middle_name: row.middle_name || '',
      phone: mappedAddr.phone,
      fax: mappedAddr.fax,
      address_first_line: mappedAddr.address_first_line,
      address_second_line: mappedAddr.address_second_line,
      address_city: mappedAddr.address_city,
      address_state: mappedAddr.address_state,
    };
  });
}

// Search providers via CMS NPI Registry (keyon/NPILookup.php).
export function searchNpiViaRegistry(form) {
  const first = (form.first || '').trim();
  const last = (form.last || '').trim();
  const npi = (form.npi || '').trim();
  if (!last && !npi) {
    return Promise.resolve([]);
  }
  const payload = {
    methname: 'GetAllInfo',
    first,
    last,
    npi,
    business: (form.business || '').trim(),
    specialty: (form.specialty || '').trim(),
  };
  if (!first && !last && npi) {
    payload.primphysician = '';
  } else {
    payload.primphysician = [first, last].filter(Boolean).join(' ');
  }
  return axios
    .post(`${CURR_API}/NPILookup.php`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((response) => mapRegistryNpiResults(response.data))
    .catch((err) => Promise.reject(err));
}

// Map a 10-digit NPI to internal LA_npi id (creates catalog row if missing).
export function resolveNpiId(npiNumber, provider = null) {
  const payload = {
    npi: String(npiNumber || '').trim(),
  };
  if (provider && typeof provider === 'object') {
    payload.first_name = provider.first_name || '';
    payload.last_name = provider.last_name || '';
    payload.middle_name = provider.middle_name || '';
    payload.phone = provider.phone || '';
    payload.fax = provider.fax || '';
    payload.address_first_line = provider.address_first_line || '';
    payload.address_second_line = provider.address_second_line || '';
    payload.address_city = provider.address_city || '';
    payload.address_state = provider.address_state || '';
  }
  const config = getRequestConfig(payload, RESOLVE_NPI_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
      .then((response) => {
        if (response && response.data && response.data.id) {
          resolve(response.data.id);
        } else if (response && response.data && response.data.message === 'npiNotInCatalog') {
          resolve(null);
        } else {
          reject('Unable to resolve provider NPI.');
        }
      })
      .catch((err) => reject(err));
  });
}

export function searchNpi(requestParams) {
  return searchNpiViaRegistry(requestParams);
}


export function searchCategory(categoryDesc) {
  const config = getRequestConfig({description: categoryDesc}, SEARCH_CATEGORY_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response && response.data && response.data.categories) {
        resolve(response.data.categories);
      } else {
        reject(`No response from search category request`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
}

export function searchProcedure(procDesc) {
  const config = getRequestConfig({description: procDesc}, SEARCH_PROCEDURE_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      // TODO: get response data
      if (response && response.data && response.data.procedures) {
        resolve(response.data.procedures);
      } else {
        reject(`No response from search procedure request`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
}

export function saveExtractionIntoReport(requestParams) {
  const config = getRequestConfig(requestParams, SAVE_EXTRACTION_ACTION);
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response && response.data.message === SAVE_EXTRACTION_SUCCESS_MESSAGE) {
        resolve(response.data);
      } else if (response && response.data.message === MISSING_CATEGORY_OR_PROCEDURE_MESSAGE) {
        reject('Your report should at least have a document type or procedure.');
      } else {
        reject(`unable to save page: ${JSON.stringify(response)}`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
  
}

export function getPreview(requestParams) {
  const config = getBlobRequestConfig(requestParams, GET_PREVIEW_ACTION);

  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response 
      && response.data 
      && response.data.message === GET_PREVIEW_SUCCESS_MESSAGE) {
        let preview = parsePdf(atob(response.data.preview));
        resolve({preview: preview});      
      } else {
        alert(' fail getPreview')

        reject(`unable to get preview: ${JSON.stringify(response)}`);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
}